import type { LegalDocumentSection } from './legal-document';
import {
  PRIVACY_POLICY_OWNER_ADDRESS,
  PRIVACY_POLICY_OWNER_EMAIL,
  PRIVACY_POLICY_OWNER_NAME,
} from './privacy-policy-content';

export const COOKIE_POLICY_VERSION = '1.0';
export const COOKIE_POLICY_EFFECTIVE_DATE = '2026-09-02';
export const COOKIE_POLICY_EFFECTIVE_DATE_LABEL = '2 de setembro de 2026';

export const cookiePolicySections: ReadonlyArray<LegalDocumentSection> = [
  {
    id: 'introducao',
    title: 'Introdução',
    blocks: [
      {
        paragraphs: [
          'O presente documento informa o Utilizador sobre as tecnologias utilizadas por esta Aplicação para as finalidades descritas abaixo. Estas tecnologias permitem ao Proprietário aceder a informação e conservá-la, por exemplo através da utilização de um Cookie, ou utilizar recursos no dispositivo do Utilizador enquanto este interage com esta Aplicação.',
          'Para simplificar, todas estas tecnologias são identificadas neste documento como "Rastreadores", salvo quando exista motivo para as distinguir. O termo Cookie é utilizado apenas quando se pretende fazer referência específica a esse tipo de Rastreador, que é próprio do navegador web.',
          'Esta Aplicação utiliza exclusivamente Rastreadores geridos diretamente pelo Proprietário, ou seja, Rastreadores próprios. Não são utilizados Rastreadores de terceiros, nem são carregados scripts, tipos de letra, mapas ou etiquetas de análise alojados por outros fornecedores nas páginas desta Aplicação.',
        ],
      },
      {
        heading: 'Não é pedido consentimento para Rastreadores',
        paragraphs: [
          'Algumas finalidades de utilização de Rastreadores exigem o consentimento do Utilizador. Não é o caso desta Aplicação: todos os Rastreadores utilizados são estritamente necessários para prestar o serviço expressamente solicitado pelo Utilizador.',
          'Por esse motivo, e nos termos do n.º 2 do artigo 5.º da Lei n.º 41/2004, de 18 de agosto, esta Aplicação não apresenta um painel de consentimento de cookies. Caso venham a ser introduzidos Rastreadores de análise estatística, de perfilagem ou de publicidade, esta política será atualizada e passará a ser pedido consentimento prévio antes da respetiva instalação.',
        ],
      },
    ],
  },
  {
    id: 'proprietario-e-responsavel',
    title: 'Proprietário e Responsável pelo Tratamento',
    blocks: [
      {
        paragraphs: [
          `${PRIVACY_POLICY_OWNER_NAME}, ${PRIVACY_POLICY_OWNER_ADDRESS}.`,
          `E-mail de contacto do Proprietário: ${PRIVACY_POLICY_OWNER_EMAIL}.`,
        ],
      },
    ],
  },
  {
    id: 'utilizacao-rastreadores',
    title: 'Como esta Aplicação utiliza Rastreadores',
    blocks: [
      {
        heading: 'Necessários',
        paragraphs: [
          'Esta Aplicação utiliza Rastreadores técnicos para realizar atividades estritamente necessárias ao seu funcionamento e à prestação do Serviço, designadamente para manter o Utilizador autenticado entre páginas.',
          'Na prática, esta Aplicação não escreve cookies no navegador. A sessão do Utilizador é guardada através do armazenamento local do navegador, uma tecnologia equivalente para efeitos legais, nos seguintes registos:',
        ],
        bullets: [
          'agendify.auth.session, no armazenamento local (localStorage): guarda a sessão autenticada quando o Utilizador opta por permanecer com sessão iniciada. Persiste até ao termo de sessão ou até ser eliminado pelo Utilizador.',
          'agendify.auth.session.temp, no armazenamento de sessão (sessionStorage): guarda a sessão autenticada quando o Utilizador não opta por permanecer com sessão iniciada. É eliminado ao fechar o separador ou o navegador.',
          '__agendify_probe__: registo técnico temporário, escrito e imediatamente apagado, utilizado apenas para verificar se o armazenamento do navegador está disponível. Não conserva quaisquer dados.',
        ],
      },
      {
        heading: 'Rastreadores não utilizados',
        paragraphs: [
          'Esta Aplicação não utiliza Rastreadores de análise estatística, de medição de audiências, de perfilagem, de retargeting, de publicidade comportamental ou de redes sociais, nem partilha dados de navegação com redes publicitárias.',
        ],
      },
    ],
  },
  {
    id: 'gerir-preferencias',
    title: 'Como gerir as preferências e controlar os Rastreadores',
    blocks: [
      {
        paragraphs: [
          'Uma vez que esta Aplicação não utiliza Rastreadores sujeitos a consentimento, não existe um painel de preferências de privacidade a apresentar. O Utilizador mantém, ainda assim, o controlo integral sobre o armazenamento efetuado no seu dispositivo através das definições do navegador.',
        ],
      },
      {
        heading: 'Controlar ou eliminar Cookies e tecnologias similares no seu dispositivo',
        paragraphs: [
          'O Utilizador pode utilizar as definições do seu navegador para ver que Cookies ou tecnologias similares foram instalados no dispositivo, bloqueá-los e eliminá-los, incluindo o armazenamento local associado a esta Aplicação. As definições do navegador não permitem, contudo, um controlo granular por categoria.',
          'Encontra informação sobre a gestão de Cookies nos navegadores mais utilizados nas seguintes páginas:',
        ],
        bullets: [
          'Google Chrome: https://support.google.com/chrome/answer/95647?hl=pt',
          'Mozilla Firefox: https://support.mozilla.org/pt-PT/kb/ative-e-desative-os-cookies-que-os-website',
          'Apple Safari: https://support.apple.com/pt-pt/guide/safari/sfri11471/mac',
          'Microsoft Edge: https://support.microsoft.com/pt-pt/microsoft-edge/eliminar-cookies-no-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09',
          'Brave: https://support.brave.com/hc/en-us/articles/360022806212-How-do-I-use-Shields-while-browsing',
          'Opera: https://help.opera.com/en/latest/web-preferences/#cookies',
        ],
      },
      {
        heading: 'Consequências da recusa dos Rastreadores',
        paragraphs: [
          'O Utilizador é livre de bloquear ou eliminar o armazenamento efetuado por esta Aplicação. Note-se, porém, que os registos indicados na secção anterior são indispensáveis para manter a sessão autenticada: bloqueando-os, não será possível iniciar sessão nem utilizar a área reservada da Aplicação. As páginas públicas de marcação continuam acessíveis.',
        ],
      },
    ],
  },
  {
    id: 'definicoes',
    title: 'Definições e referências jurídicas',
    blocks: [
      {
        heading: 'Rastreador',
        paragraphs: [
          'Qualquer tecnologia que permita o acesso a informação, a conservação de informação ou a utilização de recursos no dispositivo do Utilizador, incluindo Cookies, armazenamento local, armazenamento de sessão e tecnologias equivalentes.',
        ],
      },
      {
        heading: 'Cookie',
        paragraphs: [
          'Pequeno conjunto de dados conservado no navegador do Utilizador e reenviado ao servidor em cada pedido.',
        ],
      },
      {
        heading: 'Armazenamento local e armazenamento de sessão',
        paragraphs: [
          'Mecanismos do navegador que permitem a uma aplicação web conservar dados no dispositivo do Utilizador sem os enviar automaticamente ao servidor em cada pedido. O armazenamento de sessão é eliminado ao fechar o separador; o armazenamento local persiste até ser eliminado.',
        ],
      },
      {
        heading: 'Dados Pessoais (ou Dados)',
        paragraphs: [
          'Qualquer informação que, direta ou indiretamente, ou em articulação com outras informações, permita a identificação ou a identificabilidade de uma pessoa singular.',
        ],
      },
      {
        heading: 'Utilizador',
        paragraphs: [
          'A pessoa que utiliza esta Aplicação, coincidindo, salvo indicação em contrário, com o Titular dos Dados.',
        ],
      },
      {
        heading: 'Titular dos Dados',
        paragraphs: ['A pessoa singular a quem os Dados Pessoais se referem.'],
      },
      {
        heading: 'Responsável pelo Tratamento (ou Proprietário)',
        paragraphs: [
          'A pessoa singular ou coletiva que, individualmente ou em conjunto com outros, determina as finalidades e os meios do tratamento de Dados Pessoais. Salvo disposição em contrário, o Responsável pelo Tratamento é o Proprietário desta Aplicação.',
        ],
      },
      {
        heading: 'Esta Aplicação',
        paragraphs: [
          'O site e a aplicação BelaHora, enquanto meios através dos quais os Dados Pessoais do Utilizador são recolhidos e tratados.',
        ],
      },
      {
        paragraphs: [
          'A presente política respeita apenas a esta Aplicação, salvo indicação em contrário no presente documento, e complementa a Política de Privacidade.',
        ],
      },
    ],
  },
];
